import { Typography, Box, Tab, Tabs, Alert } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Page } from 'Common/Page/Page'
import { PageHeader, Button } from 'Common/Supplyvue-ui'
import { useState, useEffect, useMemo, useReducer } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  planDataReducer,
  PLAN_DATA_ACTIONS,
} from './CreateOperationalPlan/planDataReducer'
import { Loader } from 'Common/Loader/Loader'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'
import {
  useUpdateUserSettings,
  useUserSettings,
  useDeleteVariant,
} from './UserSettings.data'
import { getUserEmail } from 'Features/Auth/auth'
import {
  createDefaultPlanData,
  prepareTemplateData,
  calculateHorizonDateFromWeeks,
} from './CreateOperationalPlan/utils/planHelpers'
import { useSourceSites } from './OperationalPlan.data'
import { VariantForm } from './CreateOperationalPlan/shared/components'
import { MUISelect } from 'Common/Components/MUISelect'
import { VariantPrivacyType } from './OperationalPlanner.types'

const tenant = localStorage.getItem('tenantId') ?? ''
const createPlanData = createDefaultPlanData()
const horizonSelectionMode = { weeks: 'weeks', date: 'date' }

type ManageVariantMode = 'new' | 'edit' | 'delete'

interface TabPanelProps {
  children?: React.ReactNode
  index: ManageVariantMode
  value: ManageVariantMode
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`variant-tabpanel-${index}`}
      aria-labelledby={`variant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: ManageVariantMode) {
  return {
    id: `variant-tab-${index}`,
    'aria-controls': `variant-tabpanel-${index}`,
  }
}

export const ManageVariantsPage = () => {
  useBreadcrumbsData({
    plan_id: '',
  })

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get mode from URL params, default to 'new'
  const mode = (searchParams.get('mode') as ManageVariantMode) || 'new'

  const [createPlanDataObject, dispatch] = useReducer(
    planDataReducer,
    createPlanData
  )

  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [selectedVariantName, setSelectedVariantName] = useState<string>('')
  const [variantPrivacyType, setVariantPrivacyType] =
    useState<VariantPrivacyType>('public')
  const [horizonsSelectionModeState, setHorizonsSelectionModeState] = useState({
    planHorizon: horizonSelectionMode.weeks,
    constrainingHorizon: horizonSelectionMode.weeks,
  })

  const { data: sourceSiteData, isLoading: isSourceSitesLoading } =
    useSourceSites(tenant)

  useEffect(() => {
    if (userEmail === null) {
      getUserEmail().then((email: string) => {
        setUserEmail(email ? email.toLocaleLowerCase() : '')
      })
    }
    return () => {
      setUserEmail(null)
    }
  }, [])

  const { data: userSettingsData } = useUserSettings(userEmail as string)

  const handleModeChange = (
    event: React.SyntheticEvent,
    newMode: ManageVariantMode
  ) => {
    // Clear all form state when switching tabs
    setSelectedVariantName('')
    setVariantPrivacyType('public')
    setHorizonsSelectionModeState({
      planHorizon: horizonSelectionMode.weeks,
      constrainingHorizon: horizonSelectionMode.weeks,
    })
    dispatch({
      type: PLAN_DATA_ACTIONS.SET_PLAN_DATA,
      payload: createPlanData,
    })

    setSearchParams({ mode: newMode })
  }

  const handleBackToCreatePlan = () => {
    navigate('/create-plan')
  }

  const onUpdateTemplateSuccess = () => {
    toast.success('Variant saved successfully')
    setSelectedVariantName('')
  }

  const { mutate: updateUserSettings, isLoading: isTemplateSaving } =
    useUpdateUserSettings(onUpdateTemplateSuccess)

  const onDeleteVariantSuccess = () => {
    toast.success('Variant deleted successfully')
    setSelectedVariantName('')
  }

  const onDeleteVariantError = () => {
    toast.error('Failed to delete variant')
  }

  const { mutate: deleteVariant, isLoading: isDeletingVariant } =
    useDeleteVariant(onDeleteVariantSuccess, onDeleteVariantError)

  const availableVariants = useMemo(() => {
    return Object.keys(userSettingsData?.createPlanUserTemplates || {}).map(
      (key: string) => {
        const variantData = userSettingsData?.createPlanUserTemplates?.[key]
        const ownerEmail = variantData?.userEmail || userEmail
        const isOwnVariant = ownerEmail === userEmail
        const displayLabel = isOwnVariant ? key : `${key} (${ownerEmail})`

        return {
          label: displayLabel,
          value: key,
          ownerEmail,
          isOwnVariant,
        }
      }
    )
  }, [userSettingsData?.createPlanUserTemplates, userEmail])

  // Filter to only show own variants for deletion
  const ownVariantsOnly = useMemo(() => {
    return availableVariants.filter((variant) => variant.isOwnVariant)
  }, [availableVariants])

  // Helper function to check if editing another user's variant
  const isEditingOtherUserVariant = () => {
    if (!selectedVariantName || !userSettingsData?.createPlanUserTemplates) {
      return false
    }
    const variantData =
      userSettingsData.createPlanUserTemplates[selectedVariantName]
    if (!variantData || !userEmail) return false
    return variantData.userEmail !== userEmail
  }

  // Get the owner email of selected variant
  const getSelectedVariantOwnerEmail = () => {
    if (!selectedVariantName || !userSettingsData?.createPlanUserTemplates) {
      return null
    }
    const variantData =
      userSettingsData.createPlanUserTemplates[selectedVariantName]
    return variantData?.userEmail || null
  }

  // Load variant data when editing
  useEffect(() => {
    if (
      mode === 'edit' &&
      selectedVariantName &&
      userSettingsData?.createPlanUserTemplates
    ) {
      const variantData =
        userSettingsData.createPlanUserTemplates[selectedVariantName]
      if (variantData) {
        const { horizonAnchor } = variantData
        const updatedVariantData = { ...variantData }

        if (horizonAnchor?.planHorizon === 'date' && variantData.planHorizonDate) {
          updatedVariantData.planHorizonDate = new Date(variantData.planHorizonDate)
        } else {
          updatedVariantData.planHorizonDate = calculateHorizonDateFromWeeks(
            createPlanData.planStartWeek,
            variantData.planHorizon
          )
        }

        if (horizonAnchor?.constrainingHorizon === 'date' && variantData.constrainingHorizonDate) {
          updatedVariantData.constrainingHorizonDate = new Date(variantData.constrainingHorizonDate)
        } else {
          updatedVariantData.constrainingHorizonDate = calculateHorizonDateFromWeeks(
            createPlanData.planStartWeek,
            variantData.constrainingHorizon
          )
        }

        dispatch({
          type: PLAN_DATA_ACTIONS.SET_PLAN_DATA,
          payload: { ...createPlanData, ...updatedVariantData },
        })

        if (horizonAnchor) {
          setHorizonsSelectionModeState({ ...horizonAnchor })
        }

        // Set privacy type from variant data (default to public for backward compatibility)
        setVariantPrivacyType(variantData.variantPrivacyType || 'public')
      }
    }
  }, [
    mode,
    selectedVariantName,
    userSettingsData?.createPlanUserTemplates,
    dispatch,
  ])

  const handleVariantSelect = (event: any) => {
    setSelectedVariantName(event.target.value)
  }

  const handleSaveVariant = (variantName: string) => {
    if (!userEmail) return

    const templateData = prepareTemplateData(
      createPlanDataObject,
      horizonsSelectionModeState
    )

    // Determine the variant owner email
    const variantOwnerEmail = isEditingOtherUserVariant()
      ? getSelectedVariantOwnerEmail()
      : userEmail

    // Add privacy type and preserve original owner email
    const enhancedTemplateData = {
      ...templateData,
      variantPrivacyType,
      userEmail: variantOwnerEmail, // Preserve original owner, don't overwrite with current user
    }

    // Send only the new/updated variant data
    if (mode === 'new') {
      // For new variants, send only the new variant data
      updateUserSettings({
        email: userEmail,
        settings: enhancedTemplateData,
        variantName: variantName,
        isNewVariant: true,
      })
    } else {
      // For editing variants, distinguish between own and cross-user updates
      updateUserSettings({
        email: userEmail,
        settings: enhancedTemplateData,
        variantName: variantName,
        isUpdatingOwnVariant: true,
        targetUserEmail: isEditingOtherUserVariant()
          ? variantOwnerEmail
          : undefined,
      })
    }
  }

  const handleDeleteVariant = () => {
    if (!userEmail || !selectedVariantName) return

    // Only allow deleting own variants
    deleteVariant({
      email: userEmail,
      variantName: selectedVariantName,
    })
  }

  const handleCancel = () => {
    if (mode === 'new') {
      // Reset form for new mode
      dispatch({
        type: PLAN_DATA_ACTIONS.SET_PLAN_DATA,
        payload: createPlanData,
      })
    } else if (mode === 'edit') {
      setSelectedVariantName('')
    }
  }

  if (isSourceSitesLoading) {
    return <Loader />
  }

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader
          title="Manage variant"
          onBackClick={handleBackToCreatePlan}
          showBackButton={true}
        />

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={mode}
              onChange={handleModeChange}
              aria-label="variant management tabs"
            >
              <Tab label="New variant" value="new" {...a11yProps('new')} />
              <Tab label="Edit variant" value="edit" {...a11yProps('edit')} />
              <Tab
                label="Delete variant"
                value="delete"
                {...a11yProps('delete')}
              />
            </Tabs>
          </Box>

          <TabPanel value={mode} index="new">
            <Typography variant="h6" gutterBottom>
              Create new variant
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Configure your plan settings and save them as a new variant for
              future use.
            </Typography>
            {userEmail && (
              <VariantForm
                createPlanDataObject={createPlanDataObject}
                dispatch={dispatch}
                sourceSiteData={sourceSiteData || []}
                userEmail={userEmail}
                mode="new"
                variantPrivacyType={variantPrivacyType}
                onVariantPrivacyTypeChange={setVariantPrivacyType}
                onSave={handleSaveVariant}
                onCancel={handleCancel}
                isLoading={isTemplateSaving}
                horizonsSelectionModeState={horizonsSelectionModeState}
                setHorizonsSelectionModeState={setHorizonsSelectionModeState}
              />
            )}
          </TabPanel>

          <TabPanel value={mode} index="edit">
            <Typography variant="h6" gutterBottom>
              Edit existing variant
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Select a variant to edit its configuration.
            </Typography>
            {availableVariants.length > 0 ? (
              <>
                <Box sx={{ maxWidth: 400, mb: 3 }}>
                  <MUISelect
                    handleChange={handleVariantSelect}
                    selectedTemplate={selectedVariantName}
                    menuData={availableVariants}
                    label="Select variant to edit"
                  />
                </Box>
                {selectedVariantName && userEmail && (
                  <>
                    {isEditingOtherUserVariant() && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        You are editing a variant created by{' '}
                        {getSelectedVariantOwnerEmail()}. Your changes will be
                        visible to all users.
                      </Alert>
                    )}
                    <VariantForm
                      createPlanDataObject={createPlanDataObject}
                      dispatch={dispatch}
                      sourceSiteData={sourceSiteData || []}
                      userEmail={userEmail}
                      mode="edit"
                      selectedVariantName={selectedVariantName}
                      variantPrivacyType={variantPrivacyType}
                      onVariantPrivacyTypeChange={setVariantPrivacyType}
                      onSave={handleSaveVariant}
                      onCancel={handleCancel}
                      isLoading={isTemplateSaving}
                      horizonsSelectionModeState={horizonsSelectionModeState}
                      setHorizonsSelectionModeState={setHorizonsSelectionModeState}
                    />
                  </>
                )}
              </>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No variants available to edit. Create a variant first.
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={mode} index="delete">
            <Typography variant="h6" gutterBottom>
              Delete variant
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Select a variant to permanently delete. This action cannot be
              undone.
            </Typography>
            {ownVariantsOnly.length > 0 ? (
              <>
                <Box sx={{ maxWidth: 400, mb: 3 }}>
                  <MUISelect
                    handleChange={handleVariantSelect}
                    selectedTemplate={selectedVariantName}
                    menuData={ownVariantsOnly}
                    label="Select variant to delete"
                  />
                </Box>
                {selectedVariantName && (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'error.main',
                      borderRadius: 1,
                      p: 3,
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                      maxWidth: 500,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Confirm Deletion
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Are you sure you want to delete the variant "
                      {selectedVariantName}"?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                      This action cannot be undone. The variant and all its
                      settings will be permanently removed.
                    </Typography>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => setSelectedVariantName('')}
                        disabled={isDeletingVariant}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        variant="contained"
                        color="error"
                        onClick={handleDeleteVariant}
                        loading={isDeletingVariant}
                      >
                        Delete variant
                      </LoadingButton>
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No own variants available to delete.
              </Typography>
            )}
          </TabPanel>
        </Box>
      </Box>
    </Page>
  )
}
